/*
  Free-shipping toast in the cart drawer header (snippets/cart-drawer.liquid).

  Every cart change re-renders the drawer, so each render brings a fresh <ncf-shipping-toast>.
  On connect it compares its state (locked or unlocked) with the last one seen, seeded from
  the persistent <cart-drawer data-free-shipping> the page loaded with. Locked to unlocked shows
  the pill; unlocked to locked only tells screen readers how far away free shipping is again.

  The pill is aria-hidden. Screen readers get the message from the separate status line, written
  300ms after Ignite's cartUpdate or cartOpen (its own focus moves happen first), or after 1s.
  A second render inside the visible window keeps the pill up and carries the pending message.
*/
if (!customElements.get('ncf-shipping-toast')) {
  const VISIBLE_MS = 4500;
  const CLEAR_MS = 5000;

  customElements.define(
    'ncf-shipping-toast',
    class NcfShippingToast extends HTMLElement {
      static lastState = null;
      static visibleUntil = 0;
      static pendingMessage = '';

      connectedCallback() {
        const state = this.dataset.state;
        if (!state) return;

        const drawer = this.closest('cart-drawer');
        const previous = NcfShippingToast.lastState ?? (drawer ? drawer.dataset.freeShipping : null) ?? state;
        NcfShippingToast.lastState = state;

        if (previous === 'locked' && state === 'unlocked') {
          NcfShippingToast.visibleUntil = Date.now() + VISIBLE_MS;
          NcfShippingToast.pendingMessage = this.dataset.unlockedMessage || '';
          this.celebrateBand();
        } else if (previous === 'unlocked' && state === 'locked') {
          NcfShippingToast.visibleUntil = 0;
          NcfShippingToast.pendingMessage = Number(this.dataset.itemCount) > 0 ? this.bandText() : '';
        }

        const remaining = NcfShippingToast.visibleUntil - Date.now();
        if (state === 'unlocked' && remaining > 0) this.showFor(remaining);
        if (NcfShippingToast.pendingMessage) this.queueAnnouncement();
      }

      disconnectedCallback() {
        [this.hideTimer, this.fallbackTimer, this.writeTimer, this.clearTimer].forEach((timer) => clearTimeout(timer));
        (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
      }

      showFor(ms) {
        // Fade in on the first render only; a re-render mid-toast just keeps it up.
        if (ms > VISIBLE_MS - 200) {
          requestAnimationFrame(() => this.classList.add('is-visible'));
        } else {
          this.classList.add('is-visible');
        }
        this.hideTimer = setTimeout(() => this.classList.remove('is-visible'), ms);
      }

      celebrateBand() {
        const band = this.drawerPanel()?.querySelector('.drawer__shipping-bar');
        if (band) band.classList.add('drawer__shipping-bar--celebrate');
      }

      bandText() {
        const caption = this.drawerPanel()?.querySelector('.drawer__shipping-bar .caption');
        return caption ? caption.textContent.replace(/\s+/g, ' ').trim() : '';
      }

      drawerPanel() {
        return this.closest('.drawer__inner') || this.closest('cart-drawer');
      }

      queueAnnouncement() {
        const write = () => {
          if (this.writeTimer) return;
          this.writeTimer = setTimeout(() => {
            const status = this.querySelector('[data-toast-status]');
            const message = NcfShippingToast.pendingMessage;
            if (!status || !message) return;
            NcfShippingToast.pendingMessage = '';
            status.textContent = message;
            this.clearTimer = setTimeout(() => {
              status.textContent = '';
            }, CLEAR_MS);
          }, 300);
        };

        this.unsubscribers = [];
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, write));
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartOpen, write));
        }
        this.fallbackTimer = setTimeout(write, 1000);
      }
    }
  );
}
